import type { AdminPriceInsight, PriceSuggestion } from "@/types/price-insight";
import type { PricingDataSource } from "@/lib/pricing/pricingDataSource";
import { getPricingDataSourceBadgeLabel } from "@/lib/pricing/pricingDataSource";
import { supabase } from "@/lib/supabase";

class PriceInsightService {
  /**
   * Get price suggestion for an item (user-facing)
   * Uses the local Next.js API route which handles the external API call
   */
  async getPriceSuggestion(
    title: string,
    category: string,
    type: string,
    priceForSimilarity?: number,
    options?: {
      dataSource?: PricingDataSource;
      excludeItemId?: string;
      listingAttributes?: Record<string, unknown> | null;
    },
  ): Promise<PriceSuggestion> {
    try {
      const params = new URLSearchParams();
      if (options?.dataSource) {
        params.set("dataSource", options.dataSource);
      }
      const query = params.toString();
      const url = query
        ? `/api/pricing/suggest?${query}`
        : "/api/pricing/suggest";

      // Call the local Next.js API route instead of external API directly
      // This keeps environment variables on the server side
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title,
          category,
          type,
          priceForSimilarity,
          excludeItemId: options?.excludeItemId,
          listingAttributes: options?.listingAttributes,
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Failed to parse error response" }));
        throw new Error(
          errorData.error || `HTTP error! Status: ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting price suggestion:", error);
      throw error;
    }
  }

  /**
   * Get admin price insight for an item
   * Uses the admin API endpoint which supports AI providers and admin-specific features
   */
  async getAdminPriceSuggestion(
    title: string,
    category: string,
    type: string,
    options?: {
      dataSource?: PricingDataSource;
      aiProvider?: "genkit" | "openai" | "none";
      excludeItemId?: string;
      listingAttributes?: Record<string, unknown> | null;
      signal?: AbortSignal;
    },
  ): Promise<AdminPriceInsight> {
    try {
      // Validate required parameters
      if (!category || category.trim() === "") {
        throw new Error(
          "Category is required. Please ensure the item has a valid category.",
        );
      }

      // Get authentication token
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("No authentication token available");
      }

      // Build query parameters
      const params = new URLSearchParams({
        category,
      });

      if (type) {
        params.append("type", type);
      }

      if (title.trim()) {
        params.append("title", title.trim());
      }

      if (options?.dataSource) {
        params.append("dataSource", options.dataSource);
      }

      if (options?.aiProvider !== undefined) {
        params.append("aiProvider", options.aiProvider);
      }

      if (options?.excludeItemId) {
        params.append("itemId", options.excludeItemId);
      }

      if (
        options?.listingAttributes &&
        Object.keys(options.listingAttributes).length > 0
      ) {
        params.append(
          "listingAttributes",
          JSON.stringify(options.listingAttributes),
        );
      }

      // Call the admin insights endpoint
      console.log("[Admin Price Insight] Requesting:", {
        category,
        type,
        title: title.trim(),
        params: params.toString(),
      });

      const response = await fetch(`/api/admin/insights?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: options?.signal,
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Failed to parse error response" }));

        console.error("[Admin Price Insight] API error:", {
          status: response.status,
          error: errorData.error,
          message: errorData.message,
        });

        // Provide admin-specific error message
        const errorMessage =
          errorData.error ||
          errorData.message ||
          `HTTP error! Status: ${response.status}`;
        throw new Error(
          `Failed to fetch price insight: ${errorMessage}. Category: ${category}${type ? `, Type: ${type}` : ""}.`,
        );
      }

      const data = await response.json();

      console.log("[Admin Price Insight] API response:", {
        hasInsights: !!data.insights,
        insightsLength: data.insights?.length || 0,
        message: data.message,
        error: data.error,
        dataSource: data.dataSource,
        aiProvider: data.aiProvider,
        pricingDataCount: data.pricingDataCount,
      });

      // Admin endpoint returns { insights: [...], ... }
      if (data.insights && data.insights.length > 0) {
        const insight = data.insights[0] as AdminPriceInsight;
        const base: AdminPriceInsight = {
          ...insight,
          suggestedPrice: insight.suggestedPrice,
          priceRange: insight.priceRange,
          rationale: insight.rationale,
          detectedCategory: category,
          detectedType: type,
          dataSource: data.dataSource
            ? getPricingDataSourceBadgeLabel(data.dataSource)
            : insight.dataSource,
          aiProvider:
            data.aiProvider && data.aiProvider !== "none"
              ? data.aiProvider === "genkit"
                ? "Genkit AI"
                : data.aiProvider === "openai"
                  ? "OpenAI"
                  : undefined
              : undefined,
          enforcementEligible: insight.enforcementEligible,
          bandPolicy: insight.bandPolicy,
          sampleCount: insight.sampleCount ?? data.pricingDataCount,
          bandMin: insight.bandMin ?? insight.priceRange.minPrice,
          bandMax: insight.bandMax ?? insight.priceRange.maxPrice,
          bandCeilingOnly: insight.bandCeilingOnly,
          cohortTier: insight.cohortTier ?? data.cohortTier,
          cohortMatchMode: insight.cohortMatchMode ?? data.cohortMatchMode,
          exactCount: insight.exactCount ?? data.exactCount,
          cohortCompCount: insight.cohortCompCount ?? data.cohortCompCount,
          segmentCompCount: insight.segmentCompCount ?? data.segmentCompCount,
          cohortUsedForStats:
            insight.cohortUsedForStats ?? data.cohortUsedForStats,
          combinedProvenance:
            insight.combinedProvenance ?? data.combinedProvenance,
        };
        if (insight.confidenceScore != null) {
          base.confidenceScore = insight.confidenceScore;
        }
        if (insight.marketFactors) {
          base.marketFactors = insight.marketFactors;
        }
        return base;
      }

      // Handle case where no insights are returned
      // Provide admin-specific error message
      if (data.message) {
        throw new Error(
          `No pricing data available: ${data.message}. Category: ${category}${type ? `, Type: ${type}` : ""}. Please ensure there are approved items in this category.`,
        );
      }

      if (data.error) {
        // If there's an error field, use it but make it admin-friendly
        throw new Error(
          `Failed to get price insight: ${data.error}. Category: ${category}${type ? `, Type: ${type}` : ""}.`,
        );
      }

      throw new Error(
        `No pricing insight available for category: ${category}${type ? `, type: ${type}` : ""}. Please ensure there are approved items in this category.`,
      );
    } catch (error) {
      // Request was cancelled (unmount / listing change) — not a real failure.
      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }

      console.error(
        "[Admin Price Insight] Error getting admin price suggestion:",
        error,
      );

      // Ensure admin errors are always descriptive, not user-friendly messages
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";

      // If we somehow got a user-friendly error message, make it admin-appropriate
      if (
        errorMessage.includes("Price insight is not available") ||
        errorMessage.includes("continue with your listing")
      ) {
        throw new Error(
          `No pricing data found for category: ${category}${type ? `, type: ${type}` : ""}. Please ensure there are approved items in this category. The pricing insight service may not have sufficient data for this item.`,
        );
      }

      throw error;
    }
  }
}

export const priceInsightService = new PriceInsightService();
